"use client";

import React from "react";
import { FileCheck2, Compass, Droplets, DoorOpen, ClipboardCheck, Briefcase, Maximize2, TrendingUp, Tv, Users as Users2, ArrowRight, Clock, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface TimelineStep {
  id: string;
  title: string;
  duration: string;
  description: string;
  icon: React.ElementType;
}
interface LifeTrigger {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
}
const TIMELINE_FACTORS: TimelineStep[] = [{
  id: "design-finalization",
  title: "Design finalization",
  duration: "2–4 Weeks",
  description: "Architectural layouts, material selections, electrical maps, and custom storage plans are completed and confirmed.",
  icon: Compass
}, {
  id: "brookhaven-permitting",
  title: "City of Brookhaven permitting",
  duration: "3–6 Weeks",
  description: "Submitting comprehensive structural, plumbing, and electrical plans through Brookhaven municipal offices for plan review and approval.",
  icon: FileCheck2
}, {
  id: "waterproofing-assessment",
  title: "Waterproofing and moisture assessment",
  duration: "1–2 Weeks",
  description: "Foundation perimeter verification, vapor barrier inspection, subfloor moisture mitigation, and interior drainage testing.",
  icon: Droplets
}, {
  id: "egress-planning",
  title: "Egress planning",
  duration: "1–2 Weeks",
  description: "Ensuring all subterranean living rooms and bedrooms meet residential building code with walkout points or code-compliant egress wells.",
  icon: DoorOpen
}, {
  id: "inspections",
  title: "Inspections",
  duration: "Throughout Build",
  description: "Scheduled municipal milestone sign-offs for rough-in plumbing, electrical, framing, insulation, and final certificate of occupancy.",
  icon: ClipboardCheck
}];
const LIFE_TRIGGERS: LifeTrigger[] = [{
  id: "home-office",
  title: "Need a dedicated home office",
  description: "Carve out an acoustically quiet workspace separated from household foot traffic, tailored for high-focus work and virtual meetings.",
  icon: Briefcase
}, {
  id: "extra-living-space",
  title: "Need extra living space",
  description: "Transform unfinished concrete and exposed joists into comfortable daily space for relaxation, quiet retreats, or extended guests.",
  icon: Maximize2
}, {
  id: "preparing-to-sell",
  title: "Preparing to sell",
  description: "A finished basement adds substantial conditioned square footage to your appraisal, standing out in Brookhaven and DeKalb County listings.",
  icon: TrendingUp
}, {
  id: "entertainment-space",
  title: "Want entertainment space",
  description: "Build an immersive media room, game zone, custom wet bar, or golf simulator lounge designed for hosting friends and family.",
  icon: Tv
}, {
  id: "growing-family",
  title: "Growing family",
  description: "Give children and teenagers their own dedicated play space, study zone, and lounge to keep main living areas uncluttered.",
  icon: Users2
}];
export default function BasementTimeline() {
  const {
    ref: timelineRef,
    isVisible: timelineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: triggersRef,
    isVisible: triggersVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section id="basementtimelineandtriggers" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Subsection 1: Timeline Factors */}
        <div ref={timelineRef} className={`transition-all duration-700 ${timelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="max-w-3xl mb-12 lg:mb-16">
            <div className="flex items-center gap-2 text-primary font-medium text-sm tracking-wide uppercase mb-3">
              <Clock className="w-4 h-4" />
              <span>Project Planning and Milestones</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight mb-4">
              Basement remodeling timeline factors
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              Every home basement in Brookhaven has unique structural and municipal prerequisites. We manage the
              five critical factors that govern your project schedule from design to final approval.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-20 lg:mb-24">
            {/* Timeline Steps Column */}
            <div className="lg:col-span-7 space-y-4">
              {TIMELINE_FACTORS.map((step, index) => {
              const IconComponent = step.icon;
              return <Card key={step.id} data-index={index} className="border border-border/80 shadow-xs hover:border-primary/40 transition-colors bg-card">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
                            <IconComponent className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-lg font-serif font-semibold text-foreground">
                              {step.title}
                            </CardTitle>
                            <span className="text-xs font-mono uppercase tracking-wider text-primary font-medium">
                              Step {index + 1}
                            </span>
                          </div>
                        </div>
                        <span className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-md bg-muted text-foreground border border-border">
                          {step.duration}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm text-foreground/80 leading-relaxed pl-12">
                        {step.description}
                      </CardDescription>
                    </CardContent>
                  </Card>;
            })}
            </div>

            {/* Visual Photography Column */}
            <div className="lg:col-span-5 sticky top-24 space-y-6">
              <div className="overflow-hidden rounded-xl border border-border shadow-md bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/finished_basement_remodel_brookhaven.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/finished_basement_remodel_brookhaven.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/finished_basement_remodel_brookhaven.png-wvc-sizes" alt="Custom finished basement remodeling with recessed lighting, luxury vinyl plank flooring, and custom cabinetry" loading="lazy" decoding="async" className="w-full h-80 object-cover object-center" />
                <div className="p-6 bg-card border-t border-border">
                  <h3 className="text-lg font-serif font-semibold text-foreground mb-2">
                    Compliant and code-certified builds
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Brookhaven Elevation Renovations handles all City of Brookhaven architectural submittals, DeKalb
                    water authorities, and required inspections so your below-grade living area is fully permitted and
                    warranted.
                  </p>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-secondary text-secondary-foreground shadow-sm border border-secondary/80">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-md bg-primary text-primary-foreground">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-secondary-foreground mb-1">
                      Ready to check your foundation?
                    </h4>
                    <p className="text-sm text-secondary-foreground/80 mb-4">
                      Book an on-site consultation to review headroom, egress openings, and moisture controls.
                    </p>
                    <Button asChild size="sm" variant="default" className="w-full sm:w-auto">
                      <Link to="/ContactRequestAnEstimatePage">
                        Request on-site estimate
                        <ArrowRight className="w-4 h-4 ml-1.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-16 lg:my-20" />

        {/* Subsection 2: Life-Moment Triggers */}
        <div ref={triggersRef} className={`transition-all duration-700 ${triggersVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="max-w-3xl mb-12 lg:mb-16">
            <span className="text-primary font-medium text-sm tracking-wide uppercase mb-3 block">
              Life-Stage Assessment
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight mb-4">
              Is it time to finish your basement?
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              When lifestyle shifts outgrow your home's main floors, finishing the basement delivers the highest return
              on usable space without the disruption or setback constraints of an exterior addition.
            </p>
          </div>

          {/* Trigger Cards Grid with Accompanying Imagery */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {LIFE_TRIGGERS.map((trigger, idx) => {
            const IconComp = trigger.icon;
            return <Card key={trigger.id} data-index={idx} className="border border-border/80 shadow-xs hover:shadow-md transition-shadow bg-card flex flex-col justify-between">
                  <CardHeader>
                    <div className="w-10 h-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center mb-3">
                      <IconComp className="w-5 h-5 text-secondary" />
                    </div>
                    <CardTitle className="text-xl font-serif font-semibold text-foreground">
                      {trigger.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-foreground/80 leading-relaxed">
                      {trigger.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}

            {/* Showcase Visual Card */}
            <div className="rounded-xl border border-border overflow-hidden bg-card shadow-xs flex flex-col justify-between">
              <div className="relative h-48 w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_home_theater_brookhaven.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_home_theater_brookhaven.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_home_theater_brookhaven.webp-wvc-sizes" alt="Modern basement entertainment lounge and media space with built-in storage and luxury lighting" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <h3 className="font-serif font-semibold text-foreground text-lg mb-2">
                  Transform unused square footage
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Turn dark under-utilized space into your home's primary gathering floor.
                </p>
                <Button asChild variant="outline" size="sm" className="w-full">
                  <Link to="/OurProjectsPage">Explore finished projects</Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Bottom Action Strip */}
          <div className="rounded-xl bg-muted/60 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-serif font-semibold text-foreground mb-1">
                Plan your Brookhaven basement renovation
              </h3>
              <p className="text-sm text-muted-foreground">
                Discuss timeline estimates, architectural plans, and municipal requirements with our design team.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
              <Button asChild variant="default" size="lg" className="w-full sm:w-auto">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an estimate
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}